import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { C, ff, t } from '../../../shared/constants/dashboardConstants';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import { createCasting, changeCastingStatus } from '../../../api/castingApi';
import { createAdminCasting } from '../../../api/adminApi';
import { getMyProducerProfile } from '../../../api/producerApi';
import { uploadFile } from '../../../api/fileApi';

const GENRES = ['드라마', '영화', '웹드라마', 'CF·광고', '뮤지컬', '독립영화', '예능', '기타'];
const PLATFORMS = ['Netflix', 'tvN', 'MBC', 'KBS', 'SBS', 'OCN', 'JTBC', 'Wavve', 'Tving', '기타'];
const ROLE_TYPES = ['주연', '조연', '단역', '특별출연'];

const METRO_CITIES = ['인천', '부산', '대구', '대전', '광주', '울산', '세종'];
const REGION_DATA = [
  { province: '서울', cities: [] },
  { province: '광역시', cities: METRO_CITIES },
  { province: '경기', cities: ['수원', '성남', '고양', '용인', '부천', '안산', '안양', '남양주', '화성', '평택', '의정부', '시흥', '파주', '광명', '김포', '군포', '광주', '이천', '양주', '오산', '구리', '안성', '포천', '의왕', '하남', '여주', '동두천', '과천'] },
  { province: '강원', cities: ['춘천', '원주', '강릉', '동해', '태백', '속초', '삼척'] },
  { province: '충북', cities: ['청주', '충주', '제천'] },
  { province: '충남', cities: ['천안', '공주', '보령', '아산', '서산', '논산', '계룡', '당진'] },
  { province: '전북', cities: ['전주', '군산', '익산', '정읍', '남원', '김제'] },
  { province: '전남', cities: ['목포', '여수', '순천', '나주', '광양'] },
  { province: '경북', cities: ['포항', '경주', '김천', '안동', '구미', '영주', '영천', '상주', '문경', '경산'] },
  { province: '경남', cities: ['창원', '진주', '통영', '사천', '김해', '밀양', '거제', '양산'] },
  { province: '제주', cities: ['제주시', '서귀포'] },
];

const YEARS = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i - 2);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

function SectionCard({ title, children }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '20px', padding: '30px', marginBottom: '20px' }}>
      <h2 style={{ ...t('20px', 700, C.bk), margin: '0 0 24px 0' }}>{title}</h2>
      {children}
    </div>
  );
}

function FormRow({ label, required, children, extra }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
        <label style={t('14px', 600, C.bk)}>
          {label} {required && <span style={{ color: C.orange }}>*</span>}
        </label>
        {extra}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', height: '46px', padding: '0 16px', border: `1px solid ${C.g200}`,
  borderRadius: '10px', fontFamily: ff, fontSize: '15px', outline: 'none', boxSizing: 'border-box',
};

const textareaStyle = {
  ...inputStyle, height: 'auto', resize: 'vertical', minHeight: '100px', padding: '14px 16px',
};

function YearMonthPicker({ year, month, onYearChange, onMonthChange }) {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <select value={year} onChange={e => onYearChange(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
        <option value="">연도 선택</option>
        {YEARS.map(y => <option key={y} value={y}>{y}년</option>)}
      </select>
      <select value={month} onChange={e => onMonthChange(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
        <option value="">월 선택</option>
        {MONTHS.map(m => <option key={m} value={m}>{m}월</option>)}
      </select>
    </div>
  );
}

function TbdButton({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
        border: `1px solid ${active ? C.bk : C.g200}`,
        background: active ? C.bk : C.white,
        color: active ? C.white : C.sub,
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      미정
    </button>
  );
}

function LocationPicker({ value, onChange }) {
  const [province, setProvince] = useState(() => {
    if (!value) return '';
    const r = REGION_DATA.find(r => r.province === value || r.cities.includes(value));
    return r ? r.province : '';
  });

  const handleProvinceChange = (p) => {
    setProvince(p);
    onChange(p);
  };

  const cities = REGION_DATA.find(r => r.province === province)?.cities || [];

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <select
        value={province}
        onChange={e => handleProvinceChange(e.target.value)}
        style={{ ...inputStyle, flex: 1 }}
      >
        <option value="">시/도 선택</option>
        {REGION_DATA.map(r => (
          <option key={r.province} value={r.province}>{r.province === '광역시' ? '광역시 / 특별시' : r.province}</option>
        ))}
      </select>
      {cities.length > 0 && (
        <select
          value={cities.includes(value) ? value : ''}
          onChange={e => onChange(e.target.value || province)}
          style={{ ...inputStyle, flex: 1 }}
        >
          <option value="">전체 {province}</option>
          {cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
    </div>
  );
}

function RoleCard({ role, index, onChange, onRemove }) {
  return (
    <div style={{ border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '24px', position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <span style={t('16px', 700, C.bk)}>역할 {index + 1}{index === 0 && <span style={{ color: C.orange, marginLeft: '4px' }}>*</span>}</span>
        {index > 0 && (
          <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', ...t('14px', 500, '#EF4444') }}>삭제</button>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <FormRow label="역할명">
          <input value={role.name} onChange={e => onChange('name', e.target.value)} placeholder="예: 주인공 김민준" style={inputStyle} />
        </FormRow>
        <FormRow label="역할 유형">
          <select value={role.type} onChange={e => onChange('type', e.target.value)} style={inputStyle}>
            {ROLE_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </FormRow>
        <FormRow label="성별">
          <div style={{ display: 'flex', gap: '8px' }}>
            {['전체', '남성', '여성'].map(g => (
              <button key={g} onClick={() => onChange('gender', g)}
                style={{ flex: 1, padding: '10px', borderRadius: '10px', border: `1px solid ${role.gender === g ? C.bk : C.g200}`, background: role.gender === g ? C.bk : C.white, cursor: 'pointer', ...t('14px', 500, role.gender === g ? C.white : C.bk) }}>
                {g}
              </button>
            ))}
          </div>
        </FormRow>
        <FormRow label="나이 범위">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input type="number" min="0" max="100" value={role.ageMin} onChange={e => onChange('ageMin', Math.min(100, Math.max(0, e.target.value)))} placeholder="최소" style={{ ...inputStyle, flex: 1 }} />
            <span style={t('14px', 400, C.sub)}>~</span>
            <input type="number" min="0" max="100" value={role.ageMax} onChange={e => onChange('ageMax', Math.min(100, Math.max(0, e.target.value)))} placeholder="최대" style={{ ...inputStyle, flex: 1 }} />
          </div>
        </FormRow>
        <FormRow label="출연료">
          <input value={role.pay} onChange={e => onChange('pay', e.target.value)} placeholder="예: 40만원 / 10만원 (협의 시 비워두세요)" style={inputStyle} />
        </FormRow>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormRow label="역할 설명">
            <textarea value={role.desc} onChange={e => onChange('desc', e.target.value)} placeholder="역할의 성격, 특징, 요구 사항을 작성해 주세요." style={textareaStyle} />
          </FormRow>
        </div>
      </div>
    </div>
  );
}

const defaultRole = () => ({ name: '', type: '주연', gender: '전체', ageMin: '', ageMax: '', pay: '', desc: '' });
const defaultStage = (name) => ({ id: Date.now() + Math.random(), name, active: false, editing: false });

/**
 * @param {object} props
 * @param {number} [props.adminProducerProfileId] 지정하면 어드민 대행 모드로 동작한다.
 *   내 제작사 프로필을 조회하지 않고, 지정된 제작사 소유로 공고를 만든다.
 * @param {string} [props.doneRedirect] 저장 후 이동할 경로
 */
export default function ProducerCastingNewPage({ adminProducerProfileId, doneRedirect }) {
  const navigate = useNavigate();
  const { isTest } = useAuth();
  const isAdminMode = adminProducerProfileId != null;
  const [producerProfileId, setProducerProfileId] = useState(adminProducerProfileId ?? null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // 어드민 대행 모드는 대상 제작사가 이미 정해져 있다
    if (isAdminMode) return;
    // isTest 계정: 서버가 테스트 제작사 프로필을 자동 생성하므로 ID 불필요
    if (isTest) return;
    getMyProducerProfile().then(p => setProducerProfileId(p?.id ?? null)).catch(() => {});
  }, [isTest, isAdminMode]);

  const [form, setForm] = useState({
    title: '',
    genre: '',
    platform: '',
    synopsis: '',
    shootStartYear: '', shootStartMonth: '',
    shootEndYear: '', shootEndMonth: '',
    shootLocationTbd: false,
    shootLocation: '',
    shootScheduleTbd: false,
    director: '',
    applyDeadline: '',
    requirements: '',
    notes: '',
  });
  const [roles, setRoles] = useState([defaultRole()]);
  const [stages, setStages] = useState([
    defaultStage('서류 심사'),
    defaultStage('1차 선발'),
    defaultStage('2차 선발'),
    defaultStage('최종 선발'),
  ]);
  const [checklist, setChecklist] = useState({ complete: false, accurate: false, legal: false, rights: false });
  const [attachments, setAttachments] = useState([]); // [{ name, url, fileEntityId, uploading, error }]

  const MAX_ATTACHMENTS = 3;

  const handleAttachFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';

    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) return;
    const allowed = files.slice(0, remaining);

    const placeholders = allowed.map(f => ({ name: f.name, url: null, fileEntityId: null, uploading: true, error: null }));
    setAttachments(prev => [...prev, ...placeholders]);

    const startIdx = attachments.length;
    await Promise.all(allowed.map(async (file, i) => {
      const idx = startIdx + i;
      const fileType = file.type.startsWith('image/') ? 'IMAGE' : file.type.startsWith('video/') ? 'VIDEO' : 'DOCUMENT';
      try {
        const { fileEntityId, url } = await uploadFile(file, fileType);
        setAttachments(prev => prev.map((a, pi) => pi === idx ? { name: file.name, url, fileEntityId, uploading: false, error: null } : a));
      } catch {
        setAttachments(prev => prev.map((a, pi) => pi === idx ? { ...a, uploading: false, error: '업로드 실패' } : a));
      }
    }));
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const updateRole = (i, key, value) => setRoles(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: value } : r));
  const addRole = () => setRoles(prev => [...prev, defaultRole()]);
  const removeRole = (i) => setRoles(prev => prev.filter((_, idx) => idx !== i));

  const toggleStage = (id) => setStages(prev => prev.map(s => s.id === id ? { ...s, active: !s.active } : s));
  const startEditStage = (id) => setStages(prev => prev.map(s => s.id === id ? { ...s, editing: true } : s));
  const updateStageName = (id, name) => setStages(prev => prev.map(s => s.id === id ? { ...s, name } : s));
  const finishEditStage = (id) => setStages(prev => prev.map(s => s.id === id ? { ...s, editing: false } : s));
  const addStage = () => setStages(prev => {
    const newStage = { id: Date.now(), name: '새 단계', active: false, editing: true };
    return [...prev.slice(0, -1), newStage, prev[prev.length - 1]];
  });
  const deleteStage = (id) => setStages(prev => {
    const first = prev[0];
    const last = prev[prev.length - 1];
    if (id === first.id || id === last.id) return prev;
    return prev.filter(s => s.id !== id);
  });

  const handleSave = async (isDraft) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const toLocalDate = (year, month) => {
        if (!year || !month) return undefined;
        return `${year}-${String(month).padStart(2, '0')}-01`;
      };

      const payload = {
        producerProfileId,
        title: form.title,
        projectType: form.genre,
        category: form.platform,
        synopsis: form.synopsis,
        requirements: form.requirements,
        additionalInfo: form.notes,
        directorName: form.director,
        castingDirectorName: '',
        regionProvince: form.shootLocationTbd ? '' : (() => {
          const loc = form.shootLocation;
          if (!loc) return '';
          const r = REGION_DATA.find(r => r.cities.includes(loc));
          return r ? (r.province === '광역시' ? loc : r.province) : loc;
        })(),
        regionCity: form.shootLocationTbd ? '' : (() => {
          const loc = form.shootLocation;
          if (!loc) return '';
          const r = REGION_DATA.find(r => r.cities.includes(loc));
          return r ? loc : '';
        })(),
        referenceLink: '',
        shootStartDate: form.shootScheduleTbd ? undefined : toLocalDate(form.shootStartYear, form.shootStartMonth),
        shootEndDate: form.shootScheduleTbd ? undefined : toLocalDate(form.shootEndYear, form.shootEndMonth),
        applicationDeadline: form.applyDeadline,
        roles: roles.map((r, i) => ({
          roleName: r.name,
          roleType: r.type,
          gender: r.gender === '전체' ? 'ANY' : r.gender === '남성' ? 'MALE' : 'FEMALE',
          ageMin: r.ageMin ? Number(r.ageMin) : null,
          ageMax: r.ageMax ? Number(r.ageMax) : null,
          pay: r.pay || undefined,
          characterDesc: r.desc,
          sortOrder: i + 1,
        })),
        auditionStageDescriptions: stages.filter(s => s.active).map(s => s.name),
        attachmentFileIds: attachments.filter(a => a.fileEntityId).map(a => a.fileEntityId),
      };
      const result = isAdminMode
        ? await createAdminCasting(adminProducerProfileId, payload)
        : await createCasting(payload);
      if (result?.id) {
        changeCastingStatus(result.id, isDraft ? 'DRAFT' : 'OPEN').catch(() => {});
      }
      navigate(doneRedirect ?? '/producer/projects');
    } catch (err) {
      const code = err.response?.status;
      alert(code ? `저장 중 오류가 발생했습니다. (HTTP ${code})` : '저장 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const allChecked = Object.values(checklist).every(Boolean);
  const activeStages = stages.filter(s => s.active);

  return (
    <ProducerContentContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '40px' }}>
          <div>
            <h1 style={{ ...t('36px', 600, C.bk), margin: '0 0 8px 0' }}>새 공고 작성</h1>
            <span style={t('16px', 400, C.sub)}>새로운 공고를 작성하고 배우를 모집하세요.</span>
          </div>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', ...t('15px', 500, C.sub) }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            뒤로
          </button>
        </div>

        {/* 공고 기본 정보 */}
        <SectionCard title="공고 기본 정보">
          <FormRow label="공고 제목" required>
            <input value={form.title} onChange={e => updateForm('title', e.target.value)} placeholder="예: 넷플릭스 오리지날 드라마 배우 모집" style={inputStyle} />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <FormRow label="장르" required>
              <select value={form.genre} onChange={e => updateForm('genre', e.target.value)} style={inputStyle}>
                <option value="">선택하세요</option>
                {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </FormRow>
            <FormRow label="방영 플랫폼">
              <select value={form.platform} onChange={e => updateForm('platform', e.target.value)} style={inputStyle}>
                <option value="">선택하세요</option>
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormRow>
          </div>
          <FormRow label="소개" required>
            <textarea value={form.synopsis} onChange={e => updateForm('synopsis', e.target.value)} placeholder="작품의 줄거리와 핵심 내용을 작성해 주세요." style={{ ...textareaStyle, minHeight: '120px' }} />
          </FormRow>
        </SectionCard>

        {/* 촬영 정보 */}
        <SectionCard title="촬영 정보">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
            <FormRow
              label="촬영 시작"
              required
              extra={<TbdButton active={form.shootScheduleTbd} onClick={() => updateForm('shootScheduleTbd', !form.shootScheduleTbd)} />}
            >
              {!form.shootScheduleTbd && (
                <YearMonthPicker
                  year={form.shootStartYear} month={form.shootStartMonth}
                  onYearChange={v => updateForm('shootStartYear', v)}
                  onMonthChange={v => updateForm('shootStartMonth', v)}
                />
              )}
              {form.shootScheduleTbd && <div style={{ ...inputStyle, color: C.bk, display: 'flex', alignItems: 'center' }}>촬영 일정 미정</div>}
            </FormRow>
            <FormRow label="촬영 종료" required>
              {!form.shootScheduleTbd && (
                <YearMonthPicker
                  year={form.shootEndYear} month={form.shootEndMonth}
                  onYearChange={v => updateForm('shootEndYear', v)}
                  onMonthChange={v => updateForm('shootEndMonth', v)}
                />
              )}
              {form.shootScheduleTbd && <div style={{ ...inputStyle, color: C.bk, display: 'flex', alignItems: 'center' }}>촬영 일정 미정</div>}
            </FormRow>
            <FormRow
              label="촬영 장소"
              required
              extra={<TbdButton active={form.shootLocationTbd} onClick={() => { updateForm('shootLocationTbd', !form.shootLocationTbd); if (!form.shootLocationTbd) updateForm('shootLocation', ''); }} />}
            >
              {!form.shootLocationTbd
                ? <LocationPicker value={form.shootLocation} onChange={v => updateForm('shootLocation', v)} />
                : <div style={{ ...inputStyle, color: C.bk, display: 'flex', alignItems: 'center' }}>촬영 장소 미정</div>
              }
            </FormRow>
            <FormRow label="감독">
              <input value={form.director} onChange={e => updateForm('director', e.target.value)} placeholder="감독명" style={inputStyle} />
            </FormRow>
            <div style={{ gridColumn: '1 / -1' }}>
            </div>
          </div>
        </SectionCard>

        {/* 캐스팅 역할 */}
        <SectionCard title="캐스팅 역할">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
            {roles.map((role, i) => (
              <RoleCard key={i} role={role} index={i} onChange={(key, val) => updateRole(i, key, val)} onRemove={() => removeRole(i)} />
            ))}
          </div>
          <button
            onClick={addRole}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '12px', border: `1px dashed ${C.g300}`, background: C.white, cursor: 'pointer', ...t('15px', 500, C.sub), width: '100%', justifyContent: 'center' }}
          >
            <span style={{ fontSize: '18px' }}>+</span> 역할 추가
          </button>
        </SectionCard>

        {/* 공고 절차 */}
        <SectionCard title="공고 절차">
          <div style={{ marginBottom: '16px', ...t('14px', 400, C.sub) }}>진행할 공고 단계를 선택하고, 이름을 수정하거나 새로 추가할 수 있습니다.</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' }}>
            {stages.map(stage => (
              <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {stage.editing ? (
                  <input
                    autoFocus
                    value={stage.name}
                    onChange={e => updateStageName(stage.id, e.target.value)}
                    onBlur={() => finishEditStage(stage.id)}
                    onKeyDown={e => e.key === 'Enter' && finishEditStage(stage.id)}
                    style={{ padding: '8px 12px', borderRadius: '100px', border: `1px solid ${C.bk}`, fontFamily: ff, fontSize: '14px', outline: 'none', width: '120px' }}
                  />
                ) : (
                  <button
                    onClick={() => toggleStage(stage.id)}
                    style={{ padding: '10px 18px', borderRadius: '100px', border: `1px solid ${stage.active ? C.bk : C.g200}`, background: stage.active ? C.bk : C.white, cursor: 'pointer', ...t('14px', 500, stage.active ? C.white : C.bk) }}
                  >
                    {stage.name}
                  </button>
                )}
                <button
                  onClick={() => startEditStage(stage.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                  title="이름 수정"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                {stage.id !== stages[0].id && stage.id !== stages[stages.length - 1].id && (
                <button
                  onClick={() => deleteStage(stage.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                  title="단계 삭제"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
                )}
              </div>
            ))}
            <button
              onClick={addStage}
              style={{ padding: '10px 14px', borderRadius: '100px', border: `1px dashed ${C.g300}`, background: C.white, cursor: 'pointer', ...t('14px', 500, C.sub), display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <span style={{ fontSize: '16px' }}>+</span> 단계 추가
            </button>
          </div>
          {activeStages.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px 20px', background: C.bg, borderRadius: '12px', flexWrap: 'wrap' }}>
              {activeStages.map((s, i) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: C.bk, display: 'flex', alignItems: 'center', justifyContent: 'center', ...t('13px', 700, C.white) }}>{i + 1}</div>
                    <span style={t('12px', 500, C.bk)}>{s.name}</span>
                  </div>
                  {i < activeStages.length - 1 && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '16px' }}><path d="M9 18l6-6-6-6" stroke={C.g300} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* 지원 정보 */}
        <SectionCard title="지원 정보">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <FormRow label="지원 마감일" required>
              <input type="date" value={form.applyDeadline} onChange={e => updateForm('applyDeadline', e.target.value)} style={inputStyle} />
            </FormRow>
          </div>
          <FormRow label="지원요건">
            <textarea
              value={form.requirements}
              onChange={e => updateForm('requirements', e.target.value)}
              placeholder={`지원 시 필요한 요건을 작성해 주세요.\n예시:\n- 만 20세 이상\n- 기본 연기 경험자 우대\n- 제출 서류: 프로필 사진, 자기소개서\n- 동영상 자기소개 1분 이내 첨부`}
              style={{ ...textareaStyle, minHeight: '140px' }}
            />
          </FormRow>
        </SectionCard>

        {/* 추가 정보 */}
        <SectionCard title="추가 정보">
          <FormRow label="기타 안내사항">
            <textarea value={form.notes} onChange={e => updateForm('notes', e.target.value)} placeholder="기타 안내 사항이 있으면 작성해 주세요." style={textareaStyle} />
          </FormRow>

          <FormRow label="첨부파일">
            <input
              id="casting-attach-input"
              type="file"
              multiple
              disabled={attachments.length >= MAX_ATTACHMENTS}
              style={{ display: 'none' }}
              onChange={handleAttachFiles}
            />
            <label
              htmlFor={attachments.length >= MAX_ATTACHMENTS ? undefined : 'casting-attach-input'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                width: '100%', minHeight: '80px', padding: '20px', boxSizing: 'border-box',
                border: `1px solid ${C.g200}`, borderRadius: '10px',
                cursor: attachments.length >= MAX_ATTACHMENTS ? 'default' : 'pointer',
                background: attachments.length >= MAX_ATTACHMENTS ? C.bg : C.white,
                ...t('15px', 400, C.sub),
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke={attachments.length >= MAX_ATTACHMENTS ? C.g200 : C.g400} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {attachments.length >= MAX_ATTACHMENTS ? `첨부파일은 최대 ${MAX_ATTACHMENTS}개까지 가능합니다.` : `이미지, PDF, 파일 등 첨부파일을 넣어 주세요. (최대 ${MAX_ATTACHMENTS}개)`}
            </label>

            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                {attachments.map((a, idx) => {
                  const isImage = a.url && /\.(jpe?g|png|gif|webp)$/i.test(a.url);
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', border: `1px solid ${a.error ? '#FCA5A5' : C.g200}`, borderRadius: '10px', background: a.error ? '#FFF5F5' : C.white }}>
                      {isImage ? (
                        <img src={a.url} alt={a.name} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="14 2 14 8 20 8" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ ...t('14px', 500, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                        {a.uploading && <div style={t('12px', 400, C.sub)}>업로드 중...</div>}
                        {a.error && <div style={t('12px', 400, '#EF4444')}>{a.error}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </FormRow>
        </SectionCard>

        {/* 검수 체크리스트 */}
        <SectionCard title="검수 체크리스트">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {[
              { key: 'complete', label: '공고의 모든 필수 항목이 작성되었습니다.' },
              { key: 'accurate', label: '작품 정보와 캐스팅 조건이 정확합니다.' },
              { key: 'legal', label: '관련 법규 및 노동 기준을 준수합니다.' },
              { key: 'rights', label: '초상권 및 저작권 관련 사항을 확인했습니다.' },
            ].map(item => (
              <div key={item.key} onClick={() => setChecklist(prev => ({ ...prev, [item.key]: !prev[item.key] }))} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <div
                  style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${checklist[item.key] ? C.bk : C.g200}`, background: checklist[item.key] ? C.bk : C.white, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {checklist[item.key] && <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={C.white} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
                <span style={t('14px', 400, C.bk)}>{item.label}</span>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Bottom action bar */}
        <div style={{ position: 'sticky', bottom: 0, background: C.white, borderTop: `1px solid ${C.g200}`, padding: '20px 40px', margin: '0 -40px -60px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button onClick={() => navigate(-1)} style={{ padding: '14px 28px', borderRadius: '12px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('15px', 600, C.bk) }}>
            취소
          </button>
          <button onClick={() => handleSave(true)} disabled={submitting} style={{ padding: '14px 28px', borderRadius: '12px', border: `1px solid ${C.g200}`, background: C.white, cursor: submitting ? 'default' : 'pointer', ...t('15px', 600, C.bk), opacity: submitting ? 0.5 : 1 }}>
            {submitting ? '저장 중...' : '임시 저장'}
          </button>
          <button
            onClick={() => allChecked ? handleSave(false) : alert('검수 체크리스트를 모두 확인해 주세요.')}
            disabled={submitting}
            style={{ padding: '14px 28px', borderRadius: '12px', border: 'none', background: allChecked && !submitting ? C.bk : C.g200, cursor: allChecked && !submitting ? 'pointer' : 'default', ...t('15px', 600, allChecked && !submitting ? C.white : C.sub) }}
          >
            {submitting ? '등록 중...' : '공고 등록'}
          </button>
        </div>
      </ProducerContentContainer>
  );
}
